import React, { useEffect, useRef, useCallback } from 'react'
import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { FlowerAnimation } from '@/types/breathing'
import { easeOutCubic } from '@/lib/utils'

export function useThreeScene(canvasRef: React.RefObject<HTMLCanvasElement | null>) {
  const sceneRef = useRef<FlowerAnimation>({
    scene: null,
    camera: null,
    renderer: null,
    composer: null,
    flowerGroup: null,
    petals: [],
    animationId: null
  })

  const initThreeScene = useCallback(() => {
    if (!canvasRef.current) {
      console.error('Canvas ref is not available')
      return
    }

    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const width = rect.width || canvas.clientWidth || window.innerWidth
    const height = rect.height || canvas.clientHeight || window.innerHeight
    console.log('Canvas dimensions:', width, height)
    
    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000)
    camera.position.z = 5

    const renderer = new THREE.WebGLRenderer({ 
      canvas, 
      antialias: true, 
      alpha: true,
      powerPreference: 'high-performance'
    })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(width, height)
    renderer.setClearColor(0x0a0a0a, 1) // Very dark background

    // Post-processing for glow effects
    const composer = new EffectComposer(renderer)
    const renderPass = new RenderPass(scene, camera)
    composer.addPass(renderPass)

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      1.5, // strength
      0.4, // radius
      0.85 // threshold
    )
    bloomPass.threshold = 0.1
    bloomPass.strength = 1.5
    bloomPass.radius = 0.5
    composer.addPass(bloomPass)

    // Create the flower group
    const flowerGroup = new THREE.Group()

    // Center (stamen)
    const centerGeometry = new THREE.SphereGeometry(0.3, 32, 32)
    const centerMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xffff00, 
      emissive: 0xffff00, 
      emissiveIntensity: 0.5 
    })
    const center = new THREE.Mesh(centerGeometry, centerMaterial)
    flowerGroup.add(center)

    // Create petals
    const petals: THREE.Mesh[] = []
    const petalCount = 8
    
    // Create petal shape using curves
    const petalShape = new THREE.Shape()
    petalShape.moveTo(0, 0)
    petalShape.bezierCurveTo(0.5, 1, 1.5, 1, 2, 0)
    petalShape.bezierCurveTo(1.5, -1, 0.5, -1, 0, 0)

    const extrudeSettings = {
      steps: 2,
      depth: 0.1,
      bevelEnabled: true,
      bevelThickness: 0.05,
      bevelSize: 0.05,
      bevelSegments: 5
    }

    const petalGeometry = new THREE.ExtrudeGeometry(petalShape, extrudeSettings)
    const petalMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xff69b4, 
      emissive: 0xff69b4, 
      emissiveIntensity: 0.3, 
      side: THREE.DoubleSide 
    })

    for (let i = 0; i < petalCount; i++) {
      const petal = new THREE.Mesh(petalGeometry, petalMaterial)
      petal.rotation.z = (Math.PI * 2 / petalCount) * i
      petal.position.set(0, 0, 0)
      petal.scale.set(0.5, 0.5, 0.5)
      petal.userData.baseRotation = petal.rotation.z
      flowerGroup.add(petal)
      petals.push(petal)
    }

    scene.add(flowerGroup)

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5)
    scene.add(ambientLight)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
    directionalLight.position.set(5, 5, 5)
    scene.add(directionalLight)
    
    console.log('Three.js scene initialized successfully')
    console.log('Scene has', scene.children.length, 'children')
    console.log('Flower group has', flowerGroup.children.length, 'children')
    console.log('Created', petals.length, 'petals')

    // Store references
    sceneRef.current = {
      scene,
      camera,
      renderer,
      composer,
      flowerGroup,
      petals,
      animationId: null
    }

    return () => {
      // Cleanup will be handled by the separate handleResize function
    }
  }, [canvasRef])

  const handleResize = useCallback(({ width: innerWidth, height: innerHeight }: { width: number, height: number }) => {
    const { camera, renderer, composer } = sceneRef.current
    const canvas = canvasRef.current
    
    if (camera && renderer && composer && canvas) {
      // const rect = canvas.getBoundingClientRect()
      // const width = rect.width || canvas.clientWidth || window.innerWidth
      // const height = rect.height || canvas.clientHeight || window.innerHeight
      const width = innerWidth
      const height = innerHeight
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      composer.setSize(width, height)
      
      console.log('Canvas resized to:', width, height)
    }
  }, [canvasRef])

  // Add window resize listener for additional coverage
  useEffect(() => {
    const windowResizeHandler = () => {
      handleResize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', windowResizeHandler)
    
    return () => {
      window.removeEventListener('resize', windowResizeHandler)
    }
  }, [handleResize])

  const updateFlower = useCallback((cycleProgress: number, isInhaling: boolean) => {
    const { petals, flowerGroup } = sceneRef.current
    if (!petals.length || !flowerGroup) return

    // Calculate breathing factor with easing
    const breathCycle = isInhaling ? cycleProgress : 1 - cycleProgress
    const easedFactor = easeOutCubic(breathCycle)
    const openFactor = Math.max(0, Math.min(1, easedFactor))

    petals.forEach((petal) => {
      // Scale petals based on breathing cycle
      const minScale = 0.1
      const maxScale = 0.8
      const scale = minScale + (maxScale - minScale) * openFactor
      petal.scale.set(scale, scale * 1.5, scale)

      // Add slight rotation for natural movement
      petal.rotation.x = openFactor * 0.3
      petal.rotation.y = openFactor * 0.15
    })

    // Slow rotation of the entire flower
    flowerGroup.rotation.z += 0.0005
  }, [])

  const render = useCallback(() => {
    const { composer, renderer, scene, camera } = sceneRef.current
    if (composer) {
      composer.render()
    } else if (renderer && scene && camera) {
      // Fallback to direct rendering if composer fails
      renderer.render(scene, camera)
    }
  }, [])

  const startRenderLoop = useCallback(() => {
    const animate = () => {
      render()
      sceneRef.current.animationId = requestAnimationFrame(animate)
    }
    animate()
  }, [render])

  const stopRenderLoop = useCallback(() => {
    if (sceneRef.current.animationId) {
      cancelAnimationFrame(sceneRef.current.animationId)
      sceneRef.current.animationId = null
    }
  }, [])

  const cleanup = useCallback(() => {
    const { scene, renderer, animationId } = sceneRef.current
    
    if (animationId) {
      cancelAnimationFrame(animationId)
    }

    if (scene) {
      // Dispose of geometries and materials
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose()
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose())
          } else {
            object.material.dispose()
          }
        }
      })
    }

    if (renderer) {
      renderer.dispose()
    }

    // Reset the ref
    sceneRef.current = {
      scene: null,
      camera: null,
      renderer: null,
      composer: null,
      flowerGroup: null,
      petals: [],
      animationId: null
    }
  }, [])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  return {
    initThreeScene,
    updateFlower,
    render,
    startRenderLoop,
    stopRenderLoop,
    cleanup,
    handleResize,
    sceneRef: sceneRef.current
  }
}
